/**
 * Emit a SpecAst as a `WorkflowCreateInput`-shaped JSON payload that the
 * server's `/workflows/create-with-nodes` endpoint accepts.
 *
 * @module spec/emit
 */

import type { CredentialRef, JourneyNode, SpecAst } from './grammar.js';
import { normalizeDeclaredInputReferences } from './input-refs.js';

export type EmittedWorkflow = {
  name: string;
  description: string | null;
  preflight: string | null;
  cleanup: string | null;
  status?: 'draft' | 'approved';
  flowType?: 'standard' | 'setup' | 'seed' | 'teardown';
  apiOnly?: boolean;
  /** Tag names (no color/id) — diff resolves names → ids against the live tag list. */
  tags?: string[];
  /**
   * Environment eligibility, normalized to the discriminated union the API
   * accepts as `environmentConstraints` on `PUT /workflows/:id`.
   */
  environmentConstraints?:
    | { mode: 'only'; environmentNames: string[] }
    | { mode: 'envTypes'; types: Array<'permanent' | 'ephemeral' | 'local' | 'sandbox'> };
  /** Sandbox template name (not id) — resolved against the org's templates on compile. */
  sandboxTemplate?: string;
  /**
   * Teardown linkage for seed workflows. Stored as canonical fields the API
   * (and the diff layer) understand. `teardownSpec` is a workflow name that
   * sync.ts resolves to a `teardownWorkflowId` before PUT.
   */
  teardownSpec?: string;
  /** Pinned snapshot version, or `'latest'` (default) for "always use latest". */
  teardownSnapshotVersion?: 'latest' | number;
  /** Whether the linked teardown should actually run (defaults to true when set). */
  teardownEnabled?: boolean;
  /**
   * Declared outputs for seed workflows. Each entry carries spec-author intent
   * (dataKey, label, description, propertyName); sync.ts resolves propertyName
   * → propertyId and maps to the live `seedOutputsJson` row shape.
   * `credentialOutput` configs are preserved opaquely by the diff layer
   * (sidecar grammar lands in a follow-up).
   */
  seedOutputs?: Array<{
    dataKey: string;
    label?: string;
    description?: string;
    propertyName?: string;
  }>;
  inputsJson: Array<{ kind: 'value' | 'credential'; name: string; defaultValue: string }>;
  scenariosJson: Array<{
    id: string;
    name: string;
    description?: string;
    isDefault?: boolean;
    includeInSuite?: boolean;
    skipReason?: string;
    values?: Record<string, string>;
    credentials?: Record<string, string>;
  }>;
  attachments: Array<{ ref: string; source: string }>;
  nodes: Array<{
    nodeType: string;
    configJson: Record<string, unknown>;
    /** Workflow-node UUID from `<!-- @id <uuid> -->` on the lead-in (if any). */
    nodeId?: string;
    /** Source line of the lead-in in the .canary.md (for write-back). */
    sourceLine?: number;
  }>;
  /** Workflow UUID this spec is bound to, when known. */
  workflowId?: string;
  /**
   * Optional ApiSequenceConfig loaded from the sidecar YAML referenced by an
   * api_sequence node. Sync.ts POSTs it to `/api-sequences/save` after the
   * node + workflow-field sync, so the workflow gets a fresh row keyed by
   * (workflowId, …). Multiple api_sequence nodes share one sidecar (DB
   * uniqueness on workflow_id); the CLI loader picks the first non-null path
   * and ignores duplicates as long as they're identical.
   */
  apiSequenceConfig?: unknown;
};

export interface EmitOptions {
  /**
   * Append a default `end` node when the journey doesn't end in one. On by
   * default for fresh creates; the recompile/diff/pull paths pass `false` so
   * pulling a workflow that has no explicit end node doesn't spuriously add
   * one (which would show as a phantom `+1 create` on every round-trip).
   */
  appendDefaultEnd?: boolean;
}

export function emitSpec(ast: SpecAst, opts: EmitOptions = {}): EmittedWorkflow {
  const { appendDefaultEnd = true } = opts;
  const nodes: EmittedWorkflow['nodes'] = [];
  const inputNames = new Set(ast.inputs.map((i) => i.name));

  for (const n of ast.journey) emitNode(n, nodes, inputNames);

  // Compile-time login: an authenticated spec gets a login node. When an actor
  // is declared it binds that credential; without one (e.g. a pulled workflow
  // whose login resolves its credential at run/suite level) it emits a bare
  // login so the node still round-trips. The login is inserted *after* any
  // leading seed/setup nodes — those provision data before authentication, so
  // the auth login follows them rather than jumping to the very front.
  if (ast.frontmatter.authenticated) {
    let insertAt = 0;
    while (
      insertAt < nodes.length &&
      (nodes[insertAt].nodeType === 'seed' || nodes[insertAt].nodeType === 'setup')
    ) {
      insertAt++;
    }
    nodes.splice(
      insertAt,
      0,
      ast.frontmatter.actor ? loginNode(ast.frontmatter.actor) : bareLoginNode()
    );
  }

  // If the last node isn't `end`, add a default end node (fresh create only).
  const last = nodes[nodes.length - 1];
  if (appendDefaultEnd && (!last || last.nodeType !== 'end')) {
    nodes.push({
      nodeType: 'end',
      configJson: { title: 'End', description: 'Journey complete', outcome: 'pass' },
    });
  }

  return {
    name: ast.frontmatter.name,
    description: ast.spirit ?? null,
    preflight: ast.preflight ?? null,
    cleanup: ast.cleanup ?? null,
    status: ast.frontmatter.status,
    flowType: ast.frontmatter.flowType,
    apiOnly: ast.frontmatter.apiOnly,
    // Optional fields are emitted as `undefined` when absent so the diff can
    // tell "spec doesn't constrain this" (no field-update op) from "spec
    // explicitly wants the field cleared" (handled per-field in diff.ts).
    tags: ast.frontmatter.tags,
    environmentConstraints: toEnvironmentConstraints(ast.frontmatter.environments),
    sandboxTemplate: ast.frontmatter.sandboxTemplate,
    ...normalizeTeardown(ast.frontmatter.teardown),
    seedOutputs:
      ast.declaredOutputs.length > 0
        ? ast.declaredOutputs.map((o) => ({
            dataKey: o.dataKey,
            ...(o.label ? { label: o.label } : {}),
            ...(o.description ? { description: o.description } : {}),
            ...(o.propertyName ? { propertyName: o.propertyName } : {}),
          }))
        : undefined,
    inputsJson: ast.inputs.map((i) => ({
      kind: i.kind,
      name: i.name,
      defaultValue: i.defaultValue,
    })),
    scenariosJson: ast.scenarios.map((s) => ({
      id: crypto.randomUUID(),
      name: s.name,
      description: s.description,
      isDefault: s.isDefault || undefined,
      includeInSuite: s.includeInSuite,
      // `skipReason` is a spec-source-only field (no DB column). Emit it so the
      // round-trip diff doesn't flag it as drift, but sync.ts strips it before
      // PUT — the API doesn't accept it.
      ...(s.skipReason ? { skipReason: s.skipReason } : {}),
      values: Object.keys(s.values).length > 0 ? s.values : undefined,
      credentials: Object.keys(s.credentials).length > 0 ? s.credentials : undefined,
    })),
    attachments: ast.attachments,
    nodes,
    workflowId: ast.frontmatter.workflowId,
  };
}

/**
 * Translate spec `environments:` (which uses `only: [<names>]`) to the API's
 * `environmentConstraints` discriminated union (which uses
 * `environmentNames` here; sync.ts later resolves names → IDs against the
 * property's environments before PUT).
 */
function toEnvironmentConstraints(
  envs: SpecAst['frontmatter']['environments']
): EmittedWorkflow['environmentConstraints'] {
  if (!envs) return undefined;
  if ('only' in envs) return { mode: 'only', environmentNames: envs.only };
  return { mode: 'envTypes', types: envs.types };
}

/**
 * Normalize the two `teardown:` surface forms (string shorthand vs nested
 * object) to the canonical `teardownSpec`/`teardownSnapshotVersion`/
 * `teardownEnabled` triple. Shorthand string is sugar for "default behavior":
 * latest snapshot, enabled. The nested form lets the author pin a snapshot or
 * disable execution without removing the link.
 */
function normalizeTeardown(
  teardown: SpecAst['frontmatter']['teardown']
): Pick<EmittedWorkflow, 'teardownSpec' | 'teardownSnapshotVersion' | 'teardownEnabled'> {
  if (!teardown) return {};
  if (typeof teardown === 'string') {
    return { teardownSpec: teardown, teardownSnapshotVersion: 'latest', teardownEnabled: true };
  }
  return {
    teardownSpec: teardown.spec,
    teardownSnapshotVersion: teardown.snapshotVersion ?? 'latest',
    teardownEnabled: teardown.enabled ?? true,
  };
}

/** Login node with no bound credential — credential resolved at run/suite level. */
function bareLoginNode(): EmittedWorkflow['nodes'][number] {
  return {
    nodeType: 'login',
    configJson: { performLogin: true },
  };
}

function loginNode(
  actor: NonNullable<SpecAst['frontmatter']['actor']>
): EmittedWorkflow['nodes'][number] {
  return {
    nodeType: 'login',
    configJson: loginConfig(actor, {}),
  };
}

/**
 * Build the configJson for a login node from a CredentialRef. Shared by the
 * leading login (emitted from `actor:`) and mid-journey switch-role nodes so
 * both surfaces produce identical config field sets for a given identity.
 *
 * Emits resolution fields ONLY — no display labels. The credential *name* is the
 * resolution key (resolved to a concrete id server-side at the write chokepoint,
 * then stripped before persist); the "acting as …" label is derived from the
 * resolved credential at render time, never authored here.
 */
function loginConfig(
  ref: CredentialRef,
  opts: { performLogin?: boolean }
): Record<string, unknown> {
  const performLogin = opts.performLogin ?? false;
  if (ref.kind === 'credential') {
    return {
      credentialName: ref.name,
      credentialSource: 'static',
      ...(performLogin ? { performLogin: true } : {}),
    };
  }
  if (ref.kind === 'seedOutput') {
    return {
      credentialSource: 'from_seed',
      // The runtime expects an object ref; sync.ts can enrich with the seed
      // workflow id once the binding context is known. dataKey is the stable
      // identifier (matches the seed's declared output key).
      seedCredentialRef: { dataKey: ref.ref },
      // from_seed mid-journey switches always perform login; leading login
      // also performs login on session bootstrap.
      performLogin: true,
    };
  }
  return {
    credentialSource: 'from_template',
    credentialTemplateName: ref.role,
    ...(performLogin ? { performLogin: true } : {}),
  };
}

function emitNode(
  n: JourneyNode,
  out: EmittedWorkflow['nodes'],
  inputNames: ReadonlySet<string>
): void {
  const inputRef = (value: string): string => normalizeDeclaredInputReferences(value, inputNames);
  // Propagate node identity from parser → emitter → diff. nodeId is the
  // workflow-node UUID (present when the spec already has `<!-- @id ... -->`
  // on this lead-in); sourceLine is the source-file line of the lead-in,
  // captured by the parser so the writeback step can find the line again
  // without re-parsing.
  const meta: { nodeId?: string; sourceLine?: number } = {
    ...(n.nodeId ? { nodeId: n.nodeId } : {}),
    ...(n.sourceLine !== undefined ? { sourceLine: n.sourceLine } : {}),
  };
  switch (n.kind) {
    case 'navigate': {
      const pageUrl = inputRef(n.pageUrl);
      out.push({
        ...meta,
        nodeType: 'navigate',
        configJson: {
          title: `Go to ${shortPath(pageUrl)}`,
          description: inputRef(n.description ?? `Navigate to ${pageUrl}`),
          pageUrl,
          ...(n.openInNewTab ? { openInNewTab: true } : {}),
        },
      });
      return;
    }
    case 'switchRole': {
      const ref = n.credentialRef;
      out.push({
        ...meta,
        nodeType: 'login',
        configJson: loginConfig(ref, { performLogin: true }),
      });
      return;
    }
    case 'action': {
      const fieldEntries = n.fields.map((f) => ({
        name: f.name,
        label: humanizeLabel(f.name),
        type: (f.type ?? 'text') as string,
        value: inputRef(f.value),
      }));
      const inputValues =
        fieldEntries.length > 0
          ? Object.fromEntries(
              fieldEntries
                .filter((f) => f.value !== undefined && f.value !== '')
                .map((f) => [f.name, f.value])
            )
          : undefined;
      out.push({
        ...meta,
        nodeType: 'action',
        configJson: {
          customActionTitle: inputRef(n.title),
          actionDescription: inputRef(n.description ?? n.title),
          ...(fieldEntries.length > 0
            ? {
                formFields: fieldEntries,
                dataRequirements: fieldEntries.map(({ name, label, type }) => ({
                  name,
                  label,
                  type,
                  required: true,
                })),
                ...(inputValues && Object.keys(inputValues).length > 0 ? { inputValues } : {}),
              }
            : {}),
          // A declared output must be paired with a `capture` playbook step
          // carrying a matching `saveAs` (DECLARED_OUTPUT_NO_CAPTURE,
          // validation/rules/node/action.ts) — without the step the compiled
          // draft fails run validation. The step has no expression; like all
          // uncached playbook steps it guides the agent, which captures the
          // value at run time.
          ...(n.capture.length > 0
            ? {
                declaredOutputs: n.capture,
                playbookSteps: n.capture.map((name, idx) => ({
                  order: idx + 1,
                  action: 'capture',
                  target: name,
                  params: { saveAs: name },
                })),
              }
            : {}),
          ...(n.assertions.length > 0
            ? {
                inlineAssertions: n.assertions.map((a) => ({
                  condition: inputRef(a.condition),
                  severity: a.severity,
                  ...(a.agentic ? { agentic: true } : {}),
                })),
              }
            : {}),
          ...(n.captureAudio
            ? {
                captureAudio: true,
                ...(n.captureLabel ? { captureLabel: n.captureLabel } : {}),
                ...(n.captureExtraMs !== undefined ? { captureExtraMs: n.captureExtraMs } : {}),
              }
            : {}),
          ...(n.alwaysUseAgent ? { alwaysUseAgent: true } : {}),
        },
      });
      return;
    }
    case 'assertion':
      out.push({
        ...meta,
        nodeType: 'assertion',
        configJson: {
          title: 'Assert',
          description: inputRef(n.description ?? n.condition),
          condition: inputRef(n.condition),
          severity: n.severity,
          ...(n.expectedOutcome ? { expectedOutcome: n.expectedOutcome } : {}),
          ...(n.alwaysUseAgent ? { alwaysUseAgent: true } : {}),
        },
      });
      return;
    case 'seed':
      out.push({
        ...meta,
        nodeType: 'seed',
        configJson: {
          title: `Seed: ${n.specName}`,
          description: n.description ?? `Run seed workflow "${n.specName}"`,
          // Compiler resolves spec-name → seedWorkflowId; placeholder until then.
          seedWorkflowName: n.specName,
          // Emit the canonical SeedNodeConfig keys the runtime + UI actually
          // read (seed-deps.ts, SeedNodeConfig.tsx). The `seed`-prefixed names
          // are only for the AI builder-tool input schemas, which remap them;
          // here configJson is stored verbatim, so prefixed keys silently
          // revert to defaults.
          ...(n.version === 'latest' || n.version === undefined
            ? { snapshotVersion: null }
            : { snapshotVersion: n.version }),
          ...(n.onFailure ? { onFailure: n.onFailure } : {}),
          ...(n.mode ? { executionMode: n.mode } : {}),
          ...(n.apiTimeoutSeconds ? { apiTimeoutSeconds: n.apiTimeoutSeconds } : {}),
          ...(n.scope ? { scope: n.scope } : {}),
          ...(n.runTeardown !== undefined ? { runTeardown: n.runTeardown } : {}),
          // Spec `inputs` → runtime config key `inputValues` (same remap
          // convention as the other canonical-key mappings above).
          ...(n.inputs && Object.keys(n.inputs).length > 0 ? { inputValues: n.inputs } : {}),
        },
      });
      return;
    case 'setup':
      out.push({
        ...meta,
        nodeType: 'setup',
        configJson: {
          title: `Setup: ${n.specName}`,
          description: n.description ?? `Run setup workflow "${n.specName}"`,
          setupFlowName: n.specName,
          ...(n.version === 'latest' || n.version === undefined
            ? { snapshotVersion: null }
            : { snapshotVersion: n.version }),
          ...(n.mode ? { executionMode: n.mode } : {}),
        },
      });
      return;
    case 'wait':
      out.push({
        ...meta,
        nodeType: 'wait',
        configJson: {
          title: `Wait ${n.durationSeconds}s`,
          description: n.description ?? `Pause for ${n.durationSeconds} seconds`,
          waitDuration: n.durationSeconds,
          mode: 'delay',
          delaySeconds: n.durationSeconds,
        },
      });
      return;
    case 'condition':
      // Emit the condition node itself, then BOTH branches as inline siblings.
      // Real condition_true/condition_false edge wiring requires runtime work on
      // the create-with-nodes route (sequential auto-linking only today); until
      // that lands, treating branches as fallthrough is harmless — the runtime
      // walks the linear chain and a condition that doesn't branch is a no-op.
      //
      // We dropped the legacy `__specBranches` stash: both branches are now real
      // sibling nodes (the parser already produced them that way), so there's no
      // round-trip data to preserve. Diff still treats the condition node as
      // authored-by-expression only; branch identity flows through the
      // per-node @id stamps the writeback step persists.
      out.push({
        ...meta,
        nodeType: 'condition',
        configJson: {
          title: 'If',
          description: inputRef(n.description ?? n.expression),
          // Canonical runtime key is `conditionDescription`.
          conditionDescription: inputRef(n.expression),
          ...(n.hints ? { evaluationHints: inputRef(n.hints) } : {}),
        },
      });
      // Materialize both branches as fallthrough nodes after the condition.
      // Critically this PR fixes the silent-drop bug for `elseBranch` — the
      // prior implementation only emitted `thenBranch`, silently losing every
      // node the author wrote under the `Else:` block.
      n.thenBranch.forEach((c) => emitNode(c, out, inputNames));
      n.elseBranch.forEach((c) => emitNode(c, out, inputNames));
      return;
    case 'notification':
      out.push({
        ...meta,
        nodeType: 'notification',
        configJson: {
          title: `Notify ${inputRef(n.to)}`,
          description: inputRef(n.description ?? n.body),
          channel: n.channel,
          to: inputRef(n.to),
          body: inputRef(n.body),
          ...(n.subject ? { subject: inputRef(n.subject) } : {}),
          ...(n.attach ? { attachmentRef: n.attach } : {}),
        },
      });
      return;
    case 'audio':
      out.push({
        ...meta,
        nodeType: 'audio',
        configJson: {
          title: n.label ?? `Play ${n.attachmentRef}`,
          description: n.description ?? `Play audio "${n.attachmentRef}"`,
          attachmentRef: n.attachmentRef,
          ...(n.loop !== undefined ? { loop: n.loop } : {}),
          ...(n.waitForCompletion !== undefined ? { waitForCompletion: n.waitForCompletion } : {}),
          ...(n.label ? { label: n.label } : {}),
        },
      });
      return;
    case 'audioAssertion':
      out.push({
        ...meta,
        nodeType: 'audio-assertion',
        configJson: {
          title: `Assert audio: ${n.label}`,
          description: inputRef(n.description ?? n.label),
          label: n.label,
          severity: n.severity,
          ...(n.prompt
            ? {
                naturalLanguage: {
                  prompt: inputRef(n.prompt),
                  ...(n.model ? { model: n.model } : {}),
                },
              }
            : {}),
          ...(hasDeterministicAudioAssertion(n)
            ? {
                deterministic: {
                  ...(n.minDurationMs !== undefined ? { minDurationMs: n.minDurationMs } : {}),
                  ...(n.maxDurationMs !== undefined ? { maxDurationMs: n.maxDurationMs } : {}),
                  ...(n.minRmsDb !== undefined ? { minRmsDb: n.minRmsDb } : {}),
                  ...(n.silenceFractionMax !== undefined
                    ? { silenceFractionMax: n.silenceFractionMax }
                    : {}),
                  ...(n.envelopeCorrelationMin !== undefined
                    ? { envelopeCorrelationMin: n.envelopeCorrelationMin }
                    : {}),
                },
              }
            : {}),
          ...(n.reference ? { referenceAttachmentRef: n.reference } : {}),
        },
      });
      return;
    case 'apiSequence':
      out.push({
        ...meta,
        nodeType: 'api_sequence',
        configJson: {
          customActionTitle: inputRef(n.title),
          description: inputRef(n.description ?? n.title),
          apiEndpointContext: inputRef(n.apiEndpointContext),
          ...(n.credentialInputName ? { credentialInputName: n.credentialInputName } : {}),
          // Sidecar reference is metadata for the CLI sync layer — runtime
          // ignores unknown configJson keys, and the node-hash projection
          // drops it (api_sequence stays opaque at node level; the actual DSL
          // config is diffed via the sidecar load below).
          ...(n.sidecar ? { __apiSequenceSidecar: n.sidecar } : {}),
        },
      });
      return;
    case 'sandboxAction':
      out.push({
        ...meta,
        nodeType: 'sandbox_action',
        configJson: {
          title: n.label ?? `Sandbox action: ${n.actionSlug}`,
          description: inputRef(n.description ?? n.label ?? n.actionSlug),
          actionSlug: n.actionSlug,
          ...(n.label ? { actionLabel: n.label } : {}),
          // Param values pass through verbatim — $var.* / macro tokens resolve
          // at run time against the execution state.
          ...(n.params && Object.keys(n.params).length > 0 ? { params: n.params } : {}),
          ...(n.outputs && n.outputs.length > 0 ? { outputKeys: n.outputs } : {}),
        },
      });
      return;
    case 'mcp':
      out.push({
        ...meta,
        nodeType: 'mcp',
        configJson: {
          title: inputRef(n.title),
          description: inputRef(n.description ?? n.title),
          instructions: inputRef(n.instructions),
          // Name-keyed by design (like credentialTemplateName): the server's
          // write chokepoint resolves the application name → propertyId and the
          // persona name → credentialTemplateId, persisting name + id together.
          applicationName: n.application,
          ...(n.persona ? { credentialTemplateName: n.persona } : {}),
          ...(n.outputs && n.outputs.length > 0 ? { declaredOutputs: n.outputs } : {}),
          ...(n.tools && n.tools.length > 0 ? { toolAllowlist: n.tools } : {}),
        },
      });
      return;
    case 'end':
      out.push({
        ...meta,
        nodeType: 'end',
        configJson: {
          title: n.outcome === 'fail' ? 'End: fail' : 'End',
          description: n.description ?? 'Journey complete',
          outcome: n.outcome,
        },
      });
      return;
  }
}

function hasDeterministicAudioAssertion(
  n: Extract<JourneyNode, { kind: 'audioAssertion' }>
): boolean {
  return (
    n.minDurationMs !== undefined ||
    n.maxDurationMs !== undefined ||
    n.minRmsDb !== undefined ||
    n.silenceFractionMax !== undefined ||
    n.reference !== undefined ||
    n.envelopeCorrelationMin !== undefined
  );
}

function shortPath(url: string): string {
  if (url.startsWith('/')) return url;
  try {
    return new URL(url).pathname || url;
  } catch {
    return url;
  }
}

function humanizeLabel(name: string): string {
  return name.replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

// `describeNode` was previously used to stash branch summaries in the legacy
// `__specBranches` marker on condition nodes. That marker is gone now that
// both branches are materialized as real sibling nodes (see the 'condition'
// case above); the helper isn't referenced anymore and was removed to keep
// the file clean.
